export const ThemeStorageKey = 'ui-theme';
export enum Theme {
  Light = 'light',
  Dark = 'dark',
}
export type HorizonTheme = {
  themeName: Theme;
  onChange: (themeName: Theme) => void;

  // SkySpecs teal
  teal: string;
  tealActive: string;
  tealBackgroundColor: string;
  tealLightOpacity: string;

  // Background and border colors
  activeMenuItemBackgroundColor: string;
  avatarColor: string;
  borderColor: string;
  borderAccentColor: string;
  buttonBackgroundColor: string;
  buttonBorderColor: string;
  carouselBackgroundColor: string;
  contrastColor: string;
  checkboxBackgroundColor: string;
  defaultStatusColor: string;
  disabledBackground: string;
  donutChartGray: string;
  gray: string;
  inputBackgroundColor: string;
  primaryBackgroundColor: string;
  secondaryBackgroundColor: string;
  selectedListItemBackground: string;
  tableAccentColor: string;
  tableBannerBackgroundColor: string;
  tableHeaderButtonColor: string;
  tertiaryBackgroundColor: string;
  tooltipBackground: string;

  // Text colors
  descriptionColor: string;
  disabledTextColor: string;
  headerColor: string;
  labelColor: string;
  noneTextColor: string;
  secondaryTextColor: string;
  textColor: string;

  // Shadow colors
  darkShadow: string;
  lightShadow: string;
  overlay: string;

  // Damage severity colors
  severityHighColor: string;
  severityMaxColor: string;

  // 🌈
  red: string;
  backgroundRed: string;
  lightRed: string;
  orange: string;
  yellow: string;
  green: string;
  lightBlue: string;

  // Plotro colors
  plot: {
    axisColor: string;
    tickColor: string;
    bladeShapeStroke: string;
    bladeShapeFill: string;
    contourStroke: string;
    defaultPointColor: string;
    pointColors: string[];
    opacity: { less: number; default: number; more: number };
  };
};

const PLOTRO_POINT_COLORS = {
  RED: '#f5222d',
  ORANGE: '#faad14',
  BLUE: '#9bcce3',
  YELLOW: '#ffa500',
  GREEN: '#52c41a',
  PURPLE: '#9747ff',
  MAGENTA: '#ff00ff',
};

/**
 * Teal is exported to override the antd primary color in vite.config.mts
 * In general, all colors should be accessed through the theme as
 * described in `./THEME.md`
 */
export const SKYSPECS_TEAL = '#24a4ab';

export const Themes: { [key in Theme]: Omit<HorizonTheme, 'onChange'> } = {
  [Theme.Light]: {
    themeName: Theme.Light,

    // SkySpecs teal
    teal: SKYSPECS_TEAL,
    tealActive: '#44b6b8',
    tealBackgroundColor: 'rgba(36, 164, 171, 0.1)',
    tealLightOpacity: 'rgba(36, 164, 171, 0.2)',

    // Background and border colors
    activeMenuItemBackgroundColor: 'rgba(0,0,0,0.04)',
    avatarColor: '#9ccb3b',
    borderColor: '#e8e8e8',
    borderAccentColor: 'rgba(0, 0, 0, 0.1)',
    buttonBackgroundColor: '#fff',
    buttonBorderColor: '#d9d9d9',
    carouselBackgroundColor: '#ffffffd9',
    contrastColor: '#111',
    checkboxBackgroundColor: 'unset',
    defaultStatusColor: '#8c8c8c',
    disabledBackground: '#f5f5f5',
    donutChartGray: '#e6e6e6',
    gray: '#dcdfe2',
    inputBackgroundColor: '#fff',
    primaryBackgroundColor: '#f0f2f5',
    secondaryBackgroundColor: '#fff',
    selectedListItemBackground: '#f0f0f0',
    tableAccentColor: '#ddebe9',
    tableBannerBackgroundColor: 'lightyellow',
    tableHeaderButtonColor: '#bfbfbf',
    tertiaryBackgroundColor: '#fafafa',
    tooltipBackground: 'rgba(0, 0, 0, 0.75)',

    // Text colors
    descriptionColor: 'rgba(0, 0, 0, 0.75)',
    disabledTextColor: '#d9d9d9',
    headerColor: 'rgba(0,0,0,0.85)',
    labelColor: 'rgba(0, 0, 0, 0.55)',
    noneTextColor: '#ccc',
    secondaryTextColor: 'rgba(0, 0, 0, 0.45)',
    textColor: 'rgba(0, 0, 0, 0.65)',

    // Shadow colors
    darkShadow: 'rgba(0, 0, 0, 0.1)',
    lightShadow: 'rgba(0, 0, 0, 0.2)',
    overlay: 'rgba(0,0,0,0.9)',

    // Damage severity colors
    severityHighColor: '#ffa500',
    severityMaxColor: '#ff0000',

    // 🌈
    red: '#f5222d',
    backgroundRed: '#fff2f0',
    lightRed: 'rgba(245, 34, 45, 0.6)',
    orange: '#faad14',
    yellow: '#fadb14',
    green: '#52c41a',
    lightBlue: '#9bcce3',

    // Plotro colors
    plot: {
      axisColor: '#0000008c',
      tickColor: '#0000001a',
      bladeShapeFill: '#f7f7f7',
      bladeShapeStroke: '#0000001f',
      contourStroke: '#0000000d',
      defaultPointColor: '#8c8c8c',
      pointColors: Object.values(PLOTRO_POINT_COLORS),
      opacity: { less: 0.2, default: 0.6, more: 0.8 },
    },
  },
  [Theme.Dark]: {
    themeName: Theme.Dark,

    // SkySpecs teal
    teal: '#44b6b8',
    tealActive: '#66c5c7',
    tealBackgroundColor: 'rgba(36, 164, 171, 0.1)',
    tealLightOpacity: 'rgba(36, 164, 171, 0.2)',

    // Background and border colors
    activeMenuItemBackgroundColor: '#3a3a3c',
    avatarColor: SKYSPECS_TEAL,
    borderColor: '#424244',
    borderAccentColor: 'rgba(0, 0, 0, 0.1)',
    buttonBackgroundColor: '#2a2b2d',
    buttonBorderColor: '#565557',
    carouselBackgroundColor: 'rgba(0, 0, 0, 0.6)',
    contrastColor: '#fff',
    checkboxBackgroundColor: '#3b3b3b',
    defaultStatusColor: '#424244',
    disabledBackground: '#2d2d2d',
    donutChartGray: '#e6e6e6',
    gray: '#8c8c8c',
    inputBackgroundColor: '#1e1f21',
    primaryBackgroundColor: '#252628',
    secondaryBackgroundColor: '#1e1f21',
    selectedListItemBackground: '#1f1f1f',
    tableAccentColor: '#232529',
    tableBannerBackgroundColor: '#aa8c2b',
    tableHeaderButtonColor: '#636161',
    tertiaryBackgroundColor: '#2e2e30',
    tooltipBackground: '#424244',

    // Text colors
    descriptionColor: '#f5f4f3',
    disabledTextColor: '#8c8c8c',
    headerColor: '#f5f4f3',
    labelColor: '#a2a0a2',
    noneTextColor: '#8c8c8c',
    secondaryTextColor: '#fafafa',
    textColor: '#f5f4f3',

    // Shadow colors
    darkShadow: 'rgba(0, 0, 0, 0.6)',
    lightShadow: 'rgba(0, 0, 0, 0.2)',
    overlay: 'rgba(66, 66, 68, .75)',

    // Damage severity colors
    severityHighColor: '#ffa500',
    severityMaxColor: '#ff0000',

    // 🌈
    red: '#d1395a',
    backgroundRed: '#fff2f0',
    lightRed: 'rgba(245, 34, 45, 0.6)',
    orange: '#faad14',
    yellow: '#fadb14',
    green: '#158c1a',
    lightBlue: '#9bcce3',

    // Plotro colors
    plot: {
      axisColor: '#a2a0a2ab',
      tickColor: '#a2a0a26a',
      bladeShapeFill: '#66666B80',
      bladeShapeStroke: '#a2a0a21f',
      contourStroke: '#a2a0a22d',
      defaultPointColor: '#f5f4f3',
      pointColors: Object.values(PLOTRO_POINT_COLORS),
      opacity: { less: 0.4, default: 0.8, more: 0.99 },
    },
  },
};
